'use client';

import { c as _c } from "react/compiler-runtime";
import { FeatherAlertCircle, FeatherRotateCcw } from '@subframe/core';
import { Alert, Badge, Button, Loader } from '@/shared/components/ui';
import { formatDuration, formatJson, statusLabel, statusVariant } from '@/shared/formatters';
import { useLogsDaSimulacao } from '../hooks/useLogsDaSimulacao';
import type { CabecalhoDaExecucaoProps, LinhaDoPassoProps, ListaDeExecucoesProps, PassosDaExecucaoProps } from '../types';
import { dataHoraDoEvento } from '../utils/rotulosDaSimulacao';
const CARTAO = 'bg-default-background flex w-full max-w-[1160px] min-w-0 flex-col rounded-[16px] border border-solid border-neutral-200 p-6';
const LinhaDoPasso = t0 => {
  const $ = _c(25);
  const {
    passo
  } = t0;
  let t1;
  if ($[0] !== passo.saida) {
    t1 = passo.saida === null || passo.saida === undefined ? null : formatJson(passo.saida);
    $[0] = passo.saida;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const saida = t1;
  let t2;
  if ($[2] !== passo.nodeId || $[3] !== passo.stepIndex) {
    t2 = <span className="text-caption-bold font-caption-bold text-default-font">#{passo.stepIndex} · {passo.nodeId}</span>;
    $[2] = passo.nodeId;
    $[3] = passo.stepIndex;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== passo.nodeType) {
    t3 = <span className="text-caption text-subtext-color">{passo.nodeType}</span>;
    $[5] = passo.nodeType;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = passo.sucesso ? "success" : "error";
  const t5 = passo.sucesso ? "Sucesso" : "Falhou";
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <Badge variant={t4}>{t5}</Badge>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== passo.duracaoMs) {
    t7 = passo.duracaoMs !== null && <span className="text-caption text-subtext-color">{formatDuration(passo.duracaoMs)}</span>;
    $[10] = passo.duracaoMs;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t6 || $[15] !== t7) {
    t8 = <div className="flex flex-wrap items-center gap-2">{t2}{t3}{t6}{t7}</div>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== passo.erro) {
    t9 = passo.erro && <p className="text-caption text-error-600 break-words">{passo.erro}</p>;
    $[17] = passo.erro;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== saida) {
    t10 = saida && <pre className="text-caption text-default-font max-h-60 overflow-auto rounded-[8px] bg-neutral-50 p-2 whitespace-pre-wrap">{saida}</pre>;
    $[19] = saida;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t8 || $[23] !== t9) {
    t11 = <li className="flex flex-col gap-1.5 rounded-[8px] border border-solid border-neutral-200 p-3">{t8}{t9}{t10}</li>;
    $[21] = t10;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
};
const CabecalhoDaExecucao = t0 => {
  const $ = _c(28);
  const {
    execucao
  } = t0;
  let t1;
  if ($[0] !== execucao.status) {
    t1 = statusVariant(execucao.status);
    $[0] = execucao.status;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== execucao.status) {
    t2 = statusLabel(execucao.status);
    $[2] = execucao.status;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <Badge variant={t1}>{t2}</Badge>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = execucao.flowId ?? "Fluxo removido";
  let t5;
  if ($[7] !== t4) {
    t5 = <span className="text-caption-bold font-caption-bold text-default-font break-all">{t4}</span>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== execucao.entryPoint) {
    t6 = execucao.entryPoint && <span className="text-caption text-subtext-color">{execucao.entryPoint}</span>;
    $[9] = execucao.entryPoint;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t3 || $[12] !== t5 || $[13] !== t6) {
    t7 = <div className="flex flex-wrap items-center gap-2">{t3}{t5}{t6}</div>;
    $[11] = t3;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== execucao.iniciadaEm) {
    t8 = dataHoraDoEvento(execucao.iniciadaEm);
    $[15] = execucao.iniciadaEm;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== execucao.finalizadaEm) {
    t9 = execucao.finalizadaEm ? ` · Fim ${dataHoraDoEvento(execucao.finalizadaEm)}` : "";
    $[17] = execucao.finalizadaEm;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <span className="text-caption text-subtext-color">Início {t8}{t9}</span>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== execucao.erro) {
    t11 = execucao.erro && <p className="text-caption text-error-600 break-words">{execucao.erro}</p>;
    $[22] = execucao.erro;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t7) {
    t12 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t7}{t10}{t11}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t7;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
};
const PassosDaExecucao = t0 => {
  const $ = _c(24);
  const {
    logs
  } = t0;
  let t1;
  if ($[0] !== logs.erroDosPassos || $[1] !== logs.recarregarPassos) {
    t1 = logs.erroDosPassos && <div role="alert" className="text-error-600 flex flex-wrap items-center gap-2"><FeatherAlertCircle /><span className="text-caption">{logs.erroDosPassos.mensagem}</span><Button variant="neutral-secondary" size="small" onClick={logs.recarregarPassos}>Tentar novamente</Button></div>;
    $[0] = logs.erroDosPassos;
    $[1] = logs.recarregarPassos;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== logs.carregandoPassos || $[4] !== logs.passos.length) {
    t2 = logs.passos.length === 0 && logs.carregandoPassos && <div role="status" className="flex justify-center"><Loader size="small" /></div>;
    $[3] = logs.carregandoPassos;
    $[4] = logs.passos.length;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== logs.carregandoPassos || $[7] !== logs.erroDosPassos || $[8] !== logs.passos.length) {
    t3 = logs.passos.length === 0 && !logs.carregandoPassos && !logs.erroDosPassos && <p className="text-caption text-subtext-color">Nenhum passo registrado nesta execução.</p>;
    $[6] = logs.carregandoPassos;
    $[7] = logs.erroDosPassos;
    $[8] = logs.passos.length;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== logs.passos) {
    t4 = logs.passos.length > 0 && <ol aria-label={"Passos da execu\xE7\xE3o"} className="flex flex-col gap-2">{logs.passos.map(_temp)}</ol>;
    $[10] = logs.passos;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== logs.carregandoPassos || $[13] !== logs.carregarMaisPassos || $[14] !== logs.haMaisPassos || $[15] !== logs.passos.length || $[16] !== logs.totalDePassos) {
    t5 = logs.haMaisPassos && <Button variant="neutral-secondary" size="small" disabled={logs.carregandoPassos} onClick={logs.carregarMaisPassos}>Carregar mais passos ({logs.passos.length} de {logs.totalDePassos})</Button>;
    $[12] = logs.carregandoPassos;
    $[13] = logs.carregarMaisPassos;
    $[14] = logs.haMaisPassos;
    $[15] = logs.passos.length;
    $[16] = logs.totalDePassos;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== t1 || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <div className="flex flex-col gap-2">{t1}{t2}{t3}{t4}{t5}</div>;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
};
const ListaDeExecucoes = t0 => {
  const $ = _c(7);
  const {
    logs
  } = t0;
  if (!logs.iniciada) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="text-body text-subtext-color mt-4">Inicie o teste para ver os logs de execução.</p>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (logs.execucoes.length === 0 && logs.carregandoExecucoes) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div role="status" className="mt-4 flex justify-center"><Loader size="small" /></div>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (logs.execucoes.length === 0 && logs.erroDasExecucoes) {
    return null;
  }
  if (logs.execucoes.length === 0) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <p className="text-body text-subtext-color mt-4">Nenhuma execução registrada nesta simulação ainda.</p>;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== logs) {
    t1 = logs.execucoes.map(execucao => {
      const aberta = logs.execucaoAberta === execucao.id;
      return <li key={execucao.id} data-testid={`simulacao-execucao-${execucao.id}`} className="flex flex-col gap-3 rounded-[8px] border border-solid border-neutral-200 p-3"><div className="flex flex-wrap items-start gap-3"><CabecalhoDaExecucao execucao={execucao} /><Button variant="neutral-secondary" size="small" aria-expanded={aberta} onClick={() => logs.alternarExecucao(execucao.id)}>{aberta ? "Ocultar passos" : "Ver passos"}</Button></div>{aberta && <PassosDaExecucao logs={logs} />}</li>;
    });
    $[3] = logs;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <ul aria-label={"Execu\xE7\xF5es"} className="mt-4 flex flex-col gap-3">{t1}</ul>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};

/**
 * Cartão "Logs de execução" da superfície ADMIN (CA-7, CA-23). A especificação deixa a composição
 * a quem implementa; os estados são os componentes da plataforma. Saída e erro dos nós trazem o
 * conteúdo da conversa: fora do replay do RUM.
 */
export const PassosDaSimulacao = () => {
  const $ = _c(21);
  const logs = useLogsDaSimulacao();
  if (!logs.visivel) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <h2 id="logs-da-simulacao" className="text-heading-3 font-heading-3 text-default-font">Logs de execução</h2>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FeatherRotateCcw />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = !logs.iniciada || logs.carregandoExecucoes;
  let t3;
  if ($[2] !== logs.recarregarExecucoes || $[3] !== t2) {
    t3 = <div className="flex flex-wrap items-center justify-between gap-2">{t0}<Button variant="neutral-secondary" size="small" icon={t1} disabled={t2} onClick={logs.recarregarExecucoes}>Atualizar</Button></div>;
    $[2] = logs.recarregarExecucoes;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="text-body text-subtext-color mt-1">Execuções do motor nesta simulação, da mais recente para a mais antiga.</p>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== logs.erroDasExecucoes || $[7] !== logs.recarregarExecucoes) {
    t5 = logs.erroDasExecucoes && <Alert role="alert" className="mt-4" variant="error" tone="filled" icon={<FeatherAlertCircle />} title={"N\xE3o foi poss\xEDvel carregar os logs."} description={logs.erroDasExecucoes.mensagem} actions={<Button variant="neutral-secondary" size="small" onClick={logs.recarregarExecucoes}>Tentar novamente</Button>} />;
    $[6] = logs.erroDasExecucoes;
    $[7] = logs.recarregarExecucoes;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== logs) {
    t6 = <ListaDeExecucoes logs={logs} />;
    $[9] = logs;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== logs.carregandoExecucoes || $[12] !== logs.carregarMaisExecucoes || $[13] !== logs.haMaisExecucoes || $[14] !== logs.iniciada) {
    t7 = logs.iniciada && logs.haMaisExecucoes && <Button className="mt-4 self-start" variant="neutral-secondary" size="small" disabled={logs.carregandoExecucoes} onClick={logs.carregarMaisExecucoes}>Carregar mais execuções</Button>;
    $[11] = logs.carregandoExecucoes;
    $[12] = logs.carregarMaisExecucoes;
    $[13] = logs.haMaisExecucoes;
    $[14] = logs.iniciada;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t3 || $[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = <section aria-labelledby="logs-da-simulacao" data-dd-privacy="mask" className={`${CARTAO} mt-6`}>{t3}{t4}{t5}{t6}{t7}</section>;
    $[16] = t3;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
};
function _temp(passo) {
  return <LinhaDoPasso key={passo.id} passo={passo} />;
}